import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import type { TrackContribution } from '@kangaroo/shared';

import {
  countAuthoredCircuitTurns,
  resolveCircuitSetupStep,
} from '../../lib/circuitSetupFlow';

const STEP_LABELS = ['Match', 'Verify', 'Build laps', 'Save'];

type CircuitSetupGuideProps = {
  assignedContribution: TrackContribution | null;
  startFinishStatus: 'ready' | 'loading' | 'missing';
  lapCount: number;
  hasSavedLaps: boolean;
  onInspectMap?: () => void;
};

function directionLabel(contribution: TrackContribution | null) {
  if (contribution?.payload.direction === 'clockwise') return 'Clockwise';
  if (contribution?.payload.direction === 'counterclockwise') return 'Counter-clockwise';
  return 'Not set';
}

export function CircuitSetupGuide({
  assignedContribution,
  startFinishStatus,
  lapCount,
  hasSavedLaps,
  onInspectMap,
}: CircuitSetupGuideProps) {
  const theme = useTheme();
  const complete = hasSavedLaps && lapCount > 0;
  const step = resolveCircuitSetupStep({
    assigned: Boolean(assignedContribution),
    startFinishReady: startFinishStatus === 'ready',
    lapCount,
    hasSavedLaps,
  });
  const authoredTurnCount = countAuthoredCircuitTurns(assignedContribution);
  const copy = step === 0
    ? 'Choose the saved circuit that matches this session.'
    : step === 1
      ? 'Confirm the saved direction, start/finish line, and authored turn boundaries.'
      : step === 2
        ? 'Build laps from each start/finish crossing and check the result.'
        : step === 3
          ? 'Save the generated lap set so this session always opens correctly.'
          : `${lapCount} circuit laps are saved and ready to review.`;

  return (
    <Box
      sx={{
        p: 1.4,
        borderRadius: 2,
        border: '1px solid',
        borderColor: complete ? 'rgba(34,197,94,0.45)' : 'rgba(168,85,247,0.55)',
        bgcolor: complete ? 'rgba(34,197,94,0.07)' : 'rgba(109,40,217,0.11)',
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography sx={{ fontSize: '0.58rem', fontWeight: 850, letterSpacing: '0.12em', color: 'primary.light' }}>
            GUIDED SETUP
          </Typography>
          <Typography sx={{ mt: 0.15, fontSize: '0.84rem', fontWeight: 900 }}>
            {complete ? 'Circuit laps ready' : 'Circuit setup rescue'}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={complete ? 'DONE' : `${step + 1} / 4`}
          color={complete ? 'success' : 'secondary'}
          variant="outlined"
          sx={{ height: 22, fontSize: '0.58rem', fontWeight: 850 }}
        />
      </Stack>

      <Stack direction="row" spacing={0.6} sx={{ mt: 1.2 }}>
        {STEP_LABELS.map((label, index) => {
          const isComplete = index < step || step === 4;
          const isActive = index === step;
          return (
            <Stack key={label} alignItems="center" spacing={0.45} sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  border: '1px solid',
                  borderColor: isComplete
                    ? 'success.main'
                    : isActive
                      ? 'primary.main'
                      : 'divider',
                  bgcolor: isComplete
                    ? 'rgba(34,197,94,0.18)'
                    : isActive
                      ? 'rgba(168,85,247,0.2)'
                      : 'transparent',
                  color: isComplete ? 'success.light' : isActive ? 'primary.light' : 'text.disabled',
                }}
              >
                {isComplete ? <CheckRoundedIcon sx={{ fontSize: 15 }} /> : (
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 900 }}>{index + 1}</Typography>
                )}
              </Box>
              <Typography
                noWrap
                sx={{
                  maxWidth: '100%',
                  fontSize: { xs: '0.52rem', sm: '0.58rem' },
                  fontWeight: isComplete || isActive ? 800 : 650,
                  color: isComplete || isActive ? 'text.secondary' : 'text.disabled',
                }}
              >
                {label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>

      <Typography sx={{ mt: 1.15, fontSize: '0.66rem', lineHeight: 1.5, color: 'text.secondary' }}>
        {copy}
      </Typography>

      {assignedContribution ? (
        <Box sx={{ mt: 1.15, pt: 1.05, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.65, sm: 1.2 }}>
            <ValidationItem label="Direction" value={directionLabel(assignedContribution)} />
            <ValidationItem
              label="Start / finish"
              value={startFinishStatus === 'ready' ? 'Ready' : startFinishStatus === 'loading' ? 'Loading…' : 'Missing'}
              warning={startFinishStatus === 'missing'}
            />
            <ValidationItem label="Turn boundaries" value={authoredTurnCount > 0 ? String(authoredTurnCount) : 'None'} warning={authoredTurnCount === 0} />
          </Stack>
          {onInspectMap ? (
            <Button
              size="small"
              variant="text"
              startIcon={<MapOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={onInspectMap}
              sx={{ mt: 0.75, px: 0, minWidth: 0, textTransform: 'none', fontSize: '0.62rem' }}
            >
              Check direction & turns on map
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function ValidationItem({ label, value, warning = false }: { label: string; value: string; warning?: boolean }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: warning ? 'warning.main' : 'text.primary' }}>
        {value}
      </Typography>
    </Stack>
  );
}
